import { useOperatorsClientsQuery } from '@/api/queries/operators';
import useDebounce from '@/utils/hooks/useDebounce';
import { EditOutlined, Search } from '@mui/icons-material';
import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { PaginationQueryParams } from 'api/types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useOperatorTimezone from '@/utils/hooks/useOperatorTimezone';
import dayjs from 'dayjs';
import { ControlledField, DataTable, colors } from 'remote/global';
import { useClientRequestModal } from './B2BView';

function B2BMyClientsView() {
  const { t } = useTranslation();
  const { setSelectedClient, setIsClientEditModalOpen } = useClientRequestModal();
  const { control, watch, setValue } = useForm<PaginationQueryParams>({
    defaultValues: {
      search: '',
      pageNo: '0',
    },
  });
  const [search, pageNo] = watch(['search', 'pageNo']);
  const debouncedSearch = useDebounce(search, 300);
  const operatorTimezone = useOperatorTimezone();

  const { data: approvedClients, isLoading } = useOperatorsClientsQuery({
    search: debouncedSearch,
    pageNo,
  });

  const approvedColumns: GridColDef[] = [
    {
      field: 'companyName',
      headerName: t('B2BView.myclients.table.header.companyName').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'regCode',
      headerName: t('B2BView.myclients.table.header.regCode').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'companyVat',
      headerName: t('B2BView.myclients.table.header.companyVat').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'contactInfo.fullName',
      headerName: t('B2BView.myclients.table.header.contactInfo.fullName').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => row.contactInfo.fullName,
    },
    {
      field: 'contactInfo.email',
      headerName: t('B2BView.myclients.table.header.contactInfo.email').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => row.contactInfo.email,
    },
    {
      field: 'contactInfo.phone',
      headerName: t('B2BView.myclients.table.header.contactInfo.phone').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => row.contactInfo.phone,
    },
    {
      field: 'numberOfOrders',
      headerName: t('B2BView.myclients.table.header.numberOfOrders').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'lastOrderAt',
      headerName: t('B2BView.myclients.table.header.lastOrderAt').toString(),
      sortable: false,
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) =>
        row.lastOrderAt
          ? dayjs(row.lastOrderAt).tz(operatorTimezone).format('DD.MM.YYYY HH:mm')
          : '',
    },
    {
      field: 'edit',
      align: 'right',
      headerName: ' ',
      sortable: false,
      minWidth: 70,
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box display='flex' alignItems='center'>
            <IconButton
              sx={{ marginLeft: '8px' }}
              onClick={(e) => {
                setSelectedClient(row);
                setIsClientEditModalOpen((p) => !p);
                e.stopPropagation();
              }}
            >
              <EditOutlined
                sx={{
                  color: colors.uiBlue600,
                }}
              />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Typography sx={{ m: '24px 0', fontSize: '18px', fontWeight: 500, lineHeight: '26px' }}>
        {t('B2BView.myclients.table.title')}
      </Typography>
      <Grid container spacing={4}>
        <Grid item>
          <ControlledField
            control={control}
            name='search'
            fieldProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid item sx={{ width: '100%' }}>
          <DataTable
            rows={approvedClients ?? []}
            columns={approvedColumns}
            loading={isLoading}
            getRowId={(row) => row.id}
            onPageChange={(page) => setValue('pageNo', String(page))}
            t={t}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default B2BMyClientsView;
